import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, Printer } from "lucide-react";
import { useData, save } from "../hooks/useData";
import { money, num, fmtDate } from "../lib/api";
import { useAuth } from "../lib/auth";
import { PageHeader, Button, Tabs, DataTable, Modal, Field, Input, Textarea, Select } from "../components/shared";

const today = () => new Date().toISOString().slice(0, 10);
const blankLine = () => ({ lot_number: "", qty: "", rate: "", hsn: "", rolls: "" });

function LineEditor({ lines, setLines, lots, showHsn }) {
  const setL = (i, k, v) => setLines(lines.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  return (
    <div className="col-span-2 space-y-2" data-testid="fdoc-lines">
      <p className="text-xs font-semibold text-slate-500 uppercase">Fabric Lots</p>
      {lines.map((l, i) => {
        const lot = lots.find((x) => x.lot_number === l.lot_number);
        const over = lot && (parseFloat(l.qty) || 0) > (lot.balance_qty || 0);
        return (
          <div key={i} className="flex flex-wrap items-center gap-2">
            <Select className="flex-1 min-w-[14rem]" value={l.lot_number} placeholder="Select lot" data-testid={`fdoc-lot-${i}`}
              onChange={(e) => { const lt = lots.find((x) => x.lot_number === e.target.value); setLines(lines.map((x, j) => (j === i ? { ...x, lot_number: e.target.value, rate: lt?.rate ?? "" } : x))); }}
              options={lots.map((x) => ({ value: x.lot_number, label: `${x.lot_number} · ${x.fabric_type || ""} ${x.colour || ""} (bal ${num(x.balance_qty)} ${x.unit || "MTR"})` }))} />
            <Input type="number" step="any" className="w-28" placeholder="Qty" value={l.qty} onChange={(e) => setL(i, "qty", e.target.value)} data-testid={`fdoc-qty-${i}`} />
            <Input type="number" step="any" className="w-28" placeholder="Rate" value={l.rate} onChange={(e) => setL(i, "rate", e.target.value)} data-testid={`fdoc-rate-${i}`} />
            <Input className="w-20" placeholder="Rolls" value={l.rolls} onChange={(e) => setL(i, "rolls", e.target.value)} data-testid={`fdoc-rolls-${i}`} />
            {showHsn && <Input className="w-24" placeholder="HSN" value={l.hsn} onChange={(e) => setL(i, "hsn", e.target.value)} data-testid={`fdoc-hsn-${i}`} />}
            <span className="w-24 text-right text-sm tabular">{money((parseFloat(l.qty) || 0) * (parseFloat(l.rate) || 0))}</span>
            <button type="button" className="text-red-400 hover:text-red-600 disabled:opacity-30" disabled={lines.length === 1} onClick={() => setLines(lines.filter((_, j) => j !== i))}><Trash2 size={15} /></button>
            {over && <span className="basis-full text-xs text-amber-600" data-testid={`fdoc-over-${i}`}>More than the lot balance of {num(lot.balance_qty)}</span>}
          </div>
        );
      })}
      <Button type="button" size="sm" variant="secondary" onClick={() => setLines([...lines, blankLine()])} data-testid="fdoc-add-line"><Plus size={13} /> Add Lot</Button>
    </div>
  );
}

function SaleTotals({ f }) {
  const taxable = f.lines.reduce((s, l) => s + (parseFloat(l.qty) || 0) * (parseFloat(l.rate) || 0), 0);
  const tax = (taxable * (parseFloat(f.gst_rate) || 0)) / 100;
  const total = taxable + tax;
  return (
    <div className="col-span-2 bg-slate-50 rounded-md p-3 text-sm grid grid-cols-2 gap-y-1" data-testid="fsale-totals">
      <span className="text-slate-500">Taxable Value</span><span className="text-right tabular">{money(taxable)}</span>
      {f.gst_type === "IGST" ? (
        <><span className="text-slate-500">IGST @ {num(f.gst_rate || 0)}%</span><span className="text-right tabular">{money(tax)}</span></>
      ) : (
        <><span className="text-slate-500">CGST @ {num((f.gst_rate || 0) / 2)}%</span><span className="text-right tabular">{money(tax / 2)}</span>
          <span className="text-slate-500">SGST @ {num((f.gst_rate || 0) / 2)}%</span><span className="text-right tabular">{money(tax / 2)}</span></>
      )}
      <span className="font-semibold">Grand Total (rounded)</span><span className="text-right tabular font-semibold">{money(Math.round(total))}</span>
    </div>
  );
}

function DocForm({ kind, lots, suppliers, onClose, onSaved }) {
  const isSale = kind === "fabric-sales";
  const [f, setF] = useState({ date: today(), lines: [blankLine()], gst_rate: "5", gst_type: "CGST+SGST" });
  const set = (k, v) => setF({ ...f, [k]: v });
  const pickSupplier = (name) => {
    const s = suppliers.find((x) => x.name === name);
    setF({ ...f, supplier_name: name, supplier_id: s?.id, supplier_address: s?.address || "", supplier_gstin: s?.gstin || "" });
  };
  const supplierNames = [...new Set([...suppliers.map((s) => s.name), ...lots.map((l) => l.supplier_name)].filter(Boolean))];
  const submit = async (e) => {
    e.preventDefault();
    try {
      const res = await save("post", `/${kind}`, { ...f, lines: f.lines.map((l) => ({ ...l, qty: parseFloat(l.qty) || 0, rate: l.rate === "" ? undefined : parseFloat(l.rate) })) });
      toast.success(`${isSale ? "Sale Bill" : "Return Note"} ${res.number} created`);
      onSaved(res);
    } catch {}
  };
  return (
    <form onSubmit={submit} className="grid grid-cols-2 gap-4">
      <Field label="Date"><Input type="date" value={f.date} onChange={(e) => set("date", e.target.value)} data-testid="fdoc-date" /></Field>
      {isSale ? (
        <>
          <Field label="Buyer Name" required><Input value={f.buyer_name || ""} onChange={(e) => set("buyer_name", e.target.value)} required data-testid="fsale-buyer" /></Field>
          <Field label="Buyer GSTIN"><Input value={f.buyer_gstin || ""} onChange={(e) => set("buyer_gstin", e.target.value.toUpperCase())} data-testid="fsale-gstin" /></Field>
          <Field label="Buyer Phone"><Input value={f.buyer_phone || ""} onChange={(e) => set("buyer_phone", e.target.value)} data-testid="fsale-phone" /></Field>
          <Field label="Buyer Address" className="col-span-2"><Textarea value={f.buyer_address || ""} onChange={(e) => set("buyer_address", e.target.value)} data-testid="fsale-address" /></Field>
          <Field label="Place of Supply"><Input value={f.place_of_supply || ""} onChange={(e) => set("place_of_supply", e.target.value)} placeholder="e.g. Punjab" data-testid="fsale-pos" /></Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="GST %"><Input type="number" step="any" value={f.gst_rate} onChange={(e) => set("gst_rate", e.target.value)} data-testid="fsale-gst-rate" /></Field>
            <Field label="GST Type"><Select value={f.gst_type} onChange={(e) => set("gst_type", e.target.value)} options={[{ value: "CGST+SGST", label: "CGST + SGST" }, { value: "IGST", label: "IGST" }]} data-testid="fsale-gst-type" /></Field>
          </div>
        </>
      ) : (
        <>
          <Field label="Supplier" required>
            <Input list="fdoc-suppliers" value={f.supplier_name || ""} onChange={(e) => pickSupplier(e.target.value)} required placeholder="Type or pick supplier" data-testid="fret-supplier" />
            <datalist id="fdoc-suppliers">{supplierNames.map((n) => <option key={n} value={n} />)}</datalist>
          </Field>
          <Field label="Supplier GSTIN"><Input value={f.supplier_gstin || ""} onChange={(e) => set("supplier_gstin", e.target.value.toUpperCase())} data-testid="fret-gstin" /></Field>
          <Field label="Against Purchase Bill No"><Input value={f.ref_bill_number || ""} onChange={(e) => set("ref_bill_number", e.target.value)} data-testid="fret-bill" /></Field>
          <Field label="Supplier Address" className="col-span-2"><Textarea value={f.supplier_address || ""} onChange={(e) => set("supplier_address", e.target.value)} data-testid="fret-address" /></Field>
          <Field label="Reason for Return" className="col-span-2"><Input value={f.reason || ""} onChange={(e) => set("reason", e.target.value)} placeholder="e.g. shade variation, damaged rolls" data-testid="fret-reason" /></Field>
        </>
      )}
      <LineEditor lines={f.lines} setLines={(lines) => set("lines", lines)} lots={lots} showHsn={isSale} />
      {isSale && <SaleTotals f={f} />}
      <Field label="Vehicle No"><Input value={f.vehicle_number || ""} onChange={(e) => set("vehicle_number", e.target.value)} data-testid="fdoc-vehicle" /></Field>
      <Field label="Transport"><Input value={f.transport || ""} onChange={(e) => set("transport", e.target.value)} data-testid="fdoc-transport" /></Field>
      <Field label="Remarks" className="col-span-2"><Textarea value={f.remarks || ""} onChange={(e) => set("remarks", e.target.value)} data-testid="fdoc-remarks" /></Field>
      <div className="col-span-2 flex justify-end gap-2">
        <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
        <Button type="submit" data-testid="fdoc-save">{isSale ? "Create Sale Bill" : "Create Return Note"}</Button>
      </div>
    </form>
  );
}

export default function FabricDocs() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tab, setTab] = useState("fabric-returns");
  const [open, setOpen] = useState(false);
  const { data: docs, reload } = useData(`/${tab}`, [tab]);
  const { data: lots, reload: reloadLots } = useData("/fabric-lots");
  const { data: suppliers } = useData("/suppliers");
  const isSale = tab === "fabric-sales";

  const del = async (d) => {
    if (!window.confirm(`Cancel ${d.number}? The fabric quantity goes back into stock.`)) return;
    try { await save("delete", `/${tab}/${d.id}`); toast.success(`${d.number} cancelled`); reload(); reloadLots(); } catch {}
  };

  const columns = [
    { key: "number", header: isSale ? "Bill No" : "GRN No", render: (r) => <span className="font-mono text-blue-800 font-medium">{r.number}</span> },
    { key: "date", header: "Date", render: (r) => fmtDate(r.date) },
    { key: isSale ? "buyer_name" : "supplier_name", header: isSale ? "Buyer" : "Supplier" },
    { key: "lots", header: "Lots", accessor: (r) => (r.lines || []).map((l) => l.lot_number).join(", "), render: (r) => (r.lines || []).map((l) => l.lot_number).join(", ") },
    { key: "total_qty", header: "Qty", align: "right", render: (r) => num(r.total_qty) },
    isSale
      ? { key: "grand_total", header: "Grand Total", align: "right", render: (r) => money(r.grand_total) }
      : { key: "total_value", header: "Value", align: "right", render: (r) => money(r.total_value) },
    { key: "act", header: "", sortable: false, render: (r) => (
      <div className="flex items-center gap-1 justify-end" onClick={(e) => e.stopPropagation()}>
        <Button size="sm" variant="secondary" onClick={() => navigate(`/fabric-docs/${tab}/${r.id}`)} data-testid={`fdoc-print-${r.id}`}><Printer size={13} /> Print</Button>
        {user?.role === "Admin" && <button className="p-1.5 text-slate-400 hover:text-red-600" onClick={() => del(r)} title="Cancel" data-testid={`fdoc-delete-${r.id}`}><Trash2 size={15} /></button>}
      </div>
    ) },
  ];

  return (
    <div>
      <PageHeader title="Fabric Returns & Sales" subtitle="Goods return notes to suppliers and fabric sale bills. Both reduce fabric stock.">
        <Button onClick={() => setOpen(true)} data-testid="fdoc-new-btn"><Plus size={15} /> {isSale ? "New Sale Bill" : "New Return Note"}</Button>
      </PageHeader>
      <Tabs tabs={[{ value: "fabric-returns", label: "Goods Return Notes" }, { value: "fabric-sales", label: "Fabric Sale Bills" }]} active={tab} onChange={setTab} testid="fdoc-tabs" />
      <DataTable columns={columns} rows={docs || []} testid="fdoc-table" exportName={isSale ? "Fabric_Sale_Bills" : "Fabric_Return_Notes"} onRowClick={(r) => navigate(`/fabric-docs/${tab}/${r.id}`)} />
      <Modal open={open} onClose={() => setOpen(false)} title={isSale ? "New Fabric Sale Bill" : "New Goods Return Note"} xwide>
        {open && <DocForm kind={tab} lots={lots || []} suppliers={suppliers || []} onClose={() => setOpen(false)}
          onSaved={(res) => { setOpen(false); reload(); reloadLots(); navigate(`/fabric-docs/${tab}/${res.id}`); }} />}
      </Modal>
    </div>
  );
}
